// The §6 comment panel (issue #53's minimum client wiring).
//
// Deliberately small: the backend is what has to be solid here (moderation
// pipeline, authorisation, §37 sanitisation), and this panel's whole job is to
// give a player a way to read a pixel's message and, when the server actually
// grants it, a way to leave one. It never decides who may submit — a 403 from
// POST /comment is shown verbatim rather than pre-empted, because the board
// goroutine is the only thing that actually knows who captured a pixel (see
// internal/comment/capture.go), and duplicating that guess here would just be
// a second, wrong copy of it.

import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Stack, XStack, YStack } from 'tamagui';
import { PanelFrame, CornerBrackets, Hairline, Readout, UserText } from './chrome';
import {
  CommentSubmitError,
  MaxCommentRunes,
  fetchPixelComments,
  submitComment,
  type PixelCommentsResponse,
} from '../net/comments';

export interface CommentPanelProps {
  /** The hovered/targeted pixel, or null when nothing is targeted. */
  pixel: number | null;
}

export function CommentPanel({ pixel }: CommentPanelProps): ReactNode {
  const [view, setView] = useState<PixelCommentsResponse | null>(null);
  const [draft, setDraft] = useState('');
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setView(null);
    setStatus(null);
    setDraft('');
    if (pixel == null) return;
    let cancelled = false;
    fetchPixelComments(pixel)
      .then((v) => {
        if (!cancelled) setView(v);
      })
      .catch(() => {
        // A quiet miss (§6's read path is best-effort chrome, not the game).
      });
    return () => {
      cancelled = true;
    };
  }, [pixel]);

  if (pixel == null) return null;

  const runesLeft = MaxCommentRunes - Array.from(draft).length;

  const onSubmit = async (): Promise<void> => {
    if (busy || draft.trim() === '') return;
    setBusy(true);
    setStatus(null);
    try {
      await submitComment(pixel, draft);
      setStatus('Sent — pending review, Commander.');
      setDraft('');
      const fresh = await fetchPixelComments(pixel);
      setView(fresh);
    } catch (err) {
      if (err instanceof CommentSubmitError) {
        setStatus(err.message);
      } else {
        setStatus('Could not send that.');
      }
    } finally {
      setBusy(false);
    }
  };

  const live = view?.live;
  const mine = view?.mine;

  return (
    <PanelFrame width={260}>
      <CornerBrackets />
      <UserText fontSize="$4">PIXEL {String(pixel).padStart(6, '0')}</UserText>
      <Hairline />
      {live ? (
        <YStack gap={2}>
          <Readout level="small" tone="data">
            {live.body}
          </Readout>
        </YStack>
      ) : (
        <Readout level="small" tone="dim">
          NO MESSAGE
        </Readout>
      )}
      {mine && mine.state === 'PENDING' ? (
        <Readout level="small" tone="warn">
          YOUR MESSAGE: PENDING REVIEW
        </Readout>
      ) : null}
      {mine && mine.state === 'REJECTED' ? (
        <Readout level="small" tone="warn">
          YOUR MESSAGE WAS REJECTED
        </Readout>
      ) : null}
      <Hairline />
      <YStack gap="$sm">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value.slice(0, MaxCommentRunes + 40))}
          maxLength={MaxCommentRunes + 40}
          rows={2}
          placeholder="Leave a message if you took this pixel..."
          style={{
            resize: 'none',
            background: 'transparent',
            color: 'inherit',
            border: '1px solid currentColor',
            fontFamily: 'inherit',
            fontSize: 12,
            padding: 4,
          }}
        />
        <XStack alignItems="center" gap="$sm">
          <Readout level="small" tone={runesLeft < 0 ? 'warn' : 'dim'}>
            {runesLeft}
          </Readout>
          <Stack flex={1} />
          <button
            onClick={onSubmit}
            disabled={busy || draft.trim() === '' || runesLeft < 0}
            style={{ font: 'inherit', cursor: 'pointer' }}
          >
            SEND
          </button>
        </XStack>
        {status ? (
          <Readout level="small" tone="warn">
            {status}
          </Readout>
        ) : null}
      </YStack>
    </PanelFrame>
  );
}
